import { useCallback, useMemo } from 'react';
import axios from 'axios';

import useAmplitude from '@/hooks/useAmplitude';
import useEvents from '@/hooks/useEvents';
import { EVENT_TYPES } from '@/shared/constants/events';
import type { QrEngine } from '../qr/lib/engine';
import type { QrRequestStatus } from '../qr/types';
import type { QrOwnerConfig } from '../services/qrLinkOwner.service';
import type { InvoiceRequestStatus } from '../types/invoiceRequest.types';
import type { AssistantStep } from '../v2/assistant/types';
import type { StampsOrigin } from '../v2/components/SealSuccessPanel';
import type { StampedFileKind } from '../v2/components/StampedFiles';
import type { StampEngine } from '../v2/lib/stampEngine';
import type { TipoCambioErrorCode } from '../services/tipoCambio.service';
import type { PendingPaymentInvoice } from '../services/paymentComplement.service';
import type { PaymentEntry } from '../v2/lib/paymentComplement';
import { isPublicGeneralReceiver, type InvoiceDraft, type RetentionTax } from '../v2/lib/draft';
import { backdatedHoursOf } from '../v2/lib/emissionDate';
import type { CancelEngine, CancelInvoiceOutcome } from '../lib/cancellation';
import type { CancelReasonCode } from '../v2/lib/cancelReasons';
import {
  isPdfFromXml,
  type MyInvoice,
  type MyInvoiceSource,
  type MyInvoicesTab,
  type MyInvoiceStatus,
} from '../v2/lib/myInvoices';
import type { MyInvoicesFrom } from '../v2/lib/myInvoicesNavigation';
import type { PeriodPreset } from '../v2/lib/invoicePeriod';
import type { PdfBrandFieldName } from '../v2/lib/pdfBrand';
import type { QuotaPlanSource, QuotaStatus } from '../v2/lib/quota';

/**
 * Tracking de la facturación nueva y de invoice-qr v2: el ÚNICO lugar que conoce
 * los nombres `invoice_v2_*` del front y la forma de sus props.
 *
 * Contrato:
 * - El front emite intentos y pantallas. Los hechos (timbrado, sello registrado,
 *   solicitud creada o facturada) los emite heru-fiscal-engine y aquí no se repiten.
 * - Autenticado va por `useEvents` (UME + Amplitude). La página pública del QR es
 *   anónima y va solo a Amplitude: sin JWT no hay UME.
 * - Ninguna prop lleva RFC, nombre, correo ni texto libre. `user_id` lo pone el
 *   transporte; el punto de entrada se llama `entry` porque `useAmplitude` pisa `source`.
 */

export type FlowEntry =
  | 'recent_client'
  | 'search'
  | 'new_client'
  | 'publico_general'
  | 'publico_extranjero'
  | 'repeat_last'
  | 'repeat_recent_client'
  | 'repeat_generic'
  | 'draft_resumed'
  | 'assistant'
  | 'stamped_podium';

export type WizardStepViewed = 'conceptos' | 'pago' | 'revisar' | 'sellos';

export type StampsEntry = StampsOrigin;

export type CsdHomeStatus = 'valid' | 'invalid' | 'none';

/** Por qué se dio de baja el sello: vencido, o registrado con un RFC distinto al del perfil. */
export type CsdRemovalReason = 'expired' | 'rfc_mismatch';

/** `maintenance`: con `invoice-qr-maintenance` la config no se consulta, así que no se sabe. */
export type QrHomeStatus = 'none' | 'draft' | 'published' | 'maintenance';

export type QrMaintenanceSurface = 'public' | 'owner';

/** Dónde se cambió el periodo: «Mis facturas» del home, el panel del QR o todas sus solicitudes. */
export type PeriodFilterSurface = 'my_invoices' | 'qr' | 'qr_requests';

export type QrConfigEntry = 'home_cta' | 'sidebar' | 'home_banner';

/** Dónde se abrió el video de la página de facturación. */
export type QrVideoOpenedEntry = 'home' | 'qr_page';

export type QrShareChannel =
  | 'copy_link'
  | 'native_share'
  | 'whatsapp_fallback'
  | 'poster_download'
  | 'view_as_client';

/** Qué se compartió: la liga sola o la imagen del póster (que también lleva la liga). */
export type QrShareContent = 'link' | 'poster';

/** Desde dónde abrió el dueño una solicitud: la tarjeta de su página o el listado completo. */
export type QrRequestOpenedFrom = 'dashboard' | 'list';

/** Las pestañas de las solicitudes en su página: en curso (por revisar, rechazadas, fallidas) o timbradas. */
export type QrRequestsTab = 'requests' | 'stamped';

export type QrPublicLinkStatus = 'ok' | 'revoked' | 'not_found' | 'network';

export type QrPublicEntry = 'scan' | 'correction_email';

/** `error` = el validador no contestó; la página deja pasar y el timbrado vuelve a validar. */
export type QrFiscalValidationOutcome = 'valid' | 'mismatch' | 'error';

export type QrFiscalValidationTrigger = 'button' | 'submit';

export type QrSubmitErrorCode = 'validation' | 'network' | 'server';

export const QR_SUBMIT_FIELDS = ['rfc', 'name', 'postal_code', 'tax_regime'] as const;

export type QrSubmitField = (typeof QR_SUBMIT_FIELDS)[number] | 'none';

export type QrReceiptEntry = 'after_submit' | 'email';

export type QrReceiptFileKind = 'pdf' | 'xml';

/** `server` si el back contestó con error; `network` si ni siquiera contestó. */
export const qrSubmitErrorCodeOf = (error: unknown): QrSubmitErrorCode =>
  axios.isAxiosError(error) && error.response ? 'server' : 'network';

/** Estado de la liga pública para `public_viewed`, a partir del error de la consulta. */
export const qrLinkStatusOf = (error: unknown): QrPublicLinkStatus => {
  const status = axios.isAxiosError(error) ? error.response?.status : undefined;
  if (status === 410) return 'revoked';
  if (status === 404) return 'not_found';
  return 'network';
};

/**
 * Where a way to the invoicing plans was tapped; `plan_status` is the sheet of the
 * home's plan chip, `feature` the upsell of a feature the plan lacks (it carries
 * which) and `plans` is choosing a plan on its screen.
 */
export type QuotaUpgradeOrigin = 'paywall' | 'plan_status' | 'stamp_error' | 'feature' | 'plans';

/**
 * Cómo se llegó a los planes de facturación; `direct` es una liga o una recarga, sin
 * superficie, `plans_tab` es la pestaña «Facturación» de `/plans` y `onboarding` es la
 * oferta del plan después de conectar los sellos en el onboarding.
 */
export type InvoicePlansEntry = Exclude<QuotaUpgradeOrigin, 'plans'> | 'direct' | 'plans_tab' | 'onboarding';

/** El cupo como lo vio el usuario. `used` incluye lo que se está timbrando, igual que el servidor al apartar. */
export interface QuotaSnapshot {
  planCode: string | null;
  limit: number | null;
  used: number | null;
}

const quotaProps = ({ planCode, limit, used }: QuotaSnapshot) => ({
  plan_code: planCode,
  window: 'month',
  window_limit: limit,
  window_used: used,
});

/** Desde dónde se abrió la marca del PDF: la tarjeta del inicio o la URL directa. */
export type PdfBrandEntry = 'home' | 'direct';

export type PaymentStepViewed = 'factura' | 'pago' | 'revisar';

/** Lo que se hizo con la factura en la pantalla de éxito; las descargas van en `invoice_v2_file_downloaded`. */
export type StampedAction = 'view' | 'share' | 'copy_uuid' | 'invoice_another';

/** `downloaded`: no hay hoja de compartir con archivos (el WebView de Android, escritorio) y se descargó. */
export type StampedShareOutcome = 'shared' | 'downloaded' | 'cancelled';

/**
 * Lo que vio el usuario al timbrar un complemento. `uncertain` es «no sabemos si
 * se timbró» (el PAC no contestó o hay un intento en curso): ahí no se ofrece
 * reintentar con otra clave.
 */
export type PaymentStampOutcome = 'stamped' | 'already_existed' | 'rejected' | 'uncertain';

type ReceiverKind = 'saved' | 'new' | 'publico_general' | 'publico_extranjero';

const receiverKindOf = (draft: InvoiceDraft): ReceiverKind | undefined => {
  if (!draft.receiver) return undefined;
  if (isPublicGeneralReceiver(draft.receiver)) return 'publico_general';
  if (draft.receiver.mode === 'publico_extranjero') return 'publico_extranjero';
  return draft.receiver.clientId ? 'saved' : 'new';
};

const firedKeys = new Set<string>();
const stampAttempts = new Map<string, number>();

/** Guard de sesión: un mismo hecho de front no se cuenta dos veces aunque la pantalla se remonte. */
const once = (key: string, fire: () => void) => {
  if (firedKeys.has(key)) return;
  firedKeys.add(key);
  fire();
};

export interface StampRequest {
  draft: InvoiceDraft;
  total: number;
  engine: StampEngine;
  exposed: boolean;
  isTest: boolean;
  assistantSessionId?: string;
  /** Las retenciones de la regla que el usuario quitó, o `null`. */
  retentionsWaived?: Record<RetentionTax, boolean> | null;
  /** Los impuestos que se retienen a una tasa que capturó el usuario y no la regla, o `null`. */
  retentionsCaptured?: Record<RetentionTax, boolean> | null;
}

/** `isr`, `iva` o `isr_iva`: la misma etiqueta que pone el fiscal-engine en `invoice_v2_stamped`. */
const taxesLabelOf = (taxes?: Record<RetentionTax, boolean> | null): string | undefined => {
  if (!taxes) return undefined;
  if (taxes.isr && taxes.iva) return 'isr_iva';
  if (taxes.isr) return 'isr';
  if (taxes.iva) return 'iva';
  return undefined;
};

/** Pantallas, intentos y compartidos del lado autenticado: `/facturacion` y su QR. */
export const useInvoiceV2Tracking = () => {
  const { trackEvent } = useEvents();

  const emit = useCallback(
    (eventName: string, eventPayload: Record<string, unknown>) => {
      void trackEvent({ eventName, eventPayload });
    },
    [trackEvent],
  );

  return useMemo(
    () => ({
      trackHomeViewed: (props: {
        csdStatus: CsdHomeStatus;
        hasDraft: boolean;
        qrStatus: QrHomeStatus;
      }) =>
        emit(EVENT_TYPES.INVOICE_V2_HOME_VIEWED, {
          has_csd: props.csdStatus === 'valid',
          csd_status: props.csdStatus,
          has_draft: props.hasDraft,
          qr_status: props.qrStatus,
        }),

      /**
       * `repeat` solo viaja al repetir la última factura a un cliente reciente: de qué
       * motor salió (`fiscal-engine` | `nestjs`) y cuántos conceptos trae.
       */
      trackFlowStarted: (
        draft: InvoiceDraft,
        entry: FlowEntry,
        repeat?: { source: 'fiscal-engine' | 'nestjs'; conceptsCount: number },
      ) =>
        once(`flow_started:${draft.id}`, () =>
          emit(EVENT_TYPES.INVOICE_V2_FLOW_STARTED, {
            draft_id: draft.id,
            entry,
            receiver_kind: receiverKindOf(draft),
            ...(repeat ? { repeat_source: repeat.source, concepts_count: repeat.conceptsCount } : {}),
          }),
        ),

      trackStepViewed: (draftId: string, step: WizardStepViewed, entry?: StampsEntry) =>
        once(`step_viewed:${draftId}:${step}`, () =>
          emit(EVENT_TYPES.INVOICE_V2_STEP_VIEWED, {
            draft_id: draftId,
            step,
            ...(step === 'sellos' && entry ? { entry } : {}),
          }),
        ),

      trackStampRequested: ({
        draft,
        total,
        engine,
        exposed,
        isTest,
        assistantSessionId,
        retentionsWaived,
        retentionsCaptured,
      }: StampRequest) => {
        const attempt = (stampAttempts.get(draft.id) ?? 0) + 1;
        stampAttempts.set(draft.id, attempt);
        emit(EVENT_TYPES.INVOICE_V2_STAMP_REQUESTED, {
          draft_id: draft.id,
          engine,
          exposed,
          is_test: isTest,
          attempt,
          concept_count: draft.concepts.length,
          total,
          currency: draft.payment.currency,
          payment_method: draft.payment.method,
          receiver_kind: receiverKindOf(draft),
          backdated_hours: backdatedHoursOf(draft),
          ...(assistantSessionId ? { assistant_session_id: assistantSessionId } : {}),
          ...(taxesLabelOf(retentionsWaived) ? { retention_waived: taxesLabelOf(retentionsWaived) } : {}),
          ...(taxesLabelOf(retentionsCaptured)
            ? { retention_custom: taxesLabelOf(retentionsCaptured) }
            : {}),
        });
      },

      /**
       * «Mi cliente no me retiene»: quitó (`enabled: true`) o devolvió una retención de la
       * regla. Es diagnóstico del funnel; cuántas facturas salieron sin ella lo dice
       * `client_context.retention_waived` en `cfdi.emission_intent`.
       */
      trackRetentionOverrideChanged: (props: {
        draft: InvoiceDraft;
        tax: RetentionTax;
        enabled: boolean;
      }) =>
        emit(EVENT_TYPES.INVOICE_V2_RETENTION_OVERRIDE_CHANGED, {
          draft_id: props.draft.id,
          tax: props.tax,
          enabled: props.enabled,
          issuer_regime: props.draft.issuer?.taxRegime,
          receiver_kind: receiverKindOf(props.draft),
        }),

      /**
       * Capturó una tasa de retención distinta de la regla: la agregó sin regla
       * (`rule_rate` ausente) o cambió la de su régimen. `source` dice si la tecleó o
       * tocó un atajo. Una vez por borrador, impuesto y tasa. Es el funnel; cuántas
       * facturas salieron así lo dicen las tasas de `cfdi.emission_intent.payload`.
       */
      trackRetentionRateCaptured: (props: {
        draft: InvoiceDraft;
        tax: RetentionTax;
        rate: number;
        ruleRate: number | null;
        source: 'manual' | 'preset';
      }) =>
        once(`retention_rate:${props.draft.id}:${props.tax}:${props.rate}`, () =>
          emit(EVENT_TYPES.INVOICE_V2_RETENTION_RATE_CAPTURED, {
            draft_id: props.draft.id,
            tax: props.tax,
            rate: props.rate,
            ...(props.ruleRate !== null ? { rule_rate: props.ruleRate } : {}),
            source: props.source,
            issuer_regime: props.draft.issuer?.taxRegime,
            receiver_kind: receiverKindOf(props.draft),
          }),
        ),

      /**
       * El alta de un cliente que salió de «Clientes a los que ya les facturaste», ya
       * confirmada por el POST. Hasta que el fiscal-engine guarde el origen del alta, este
       * es su único registro.
       *
       * `address_book_id` es el `id` que regresó el alta (`public.receiver.id`): con el
       * `user_id` lleva a `public.receiver.rfc` y de ahí a `cfdi.emission_intent`
       * (`payload->'receiver'->>'rfc'`) sin que el RFC salga del servidor. `position` es el
       * lugar del cliente en la lista completa (base 0, del más reciente al más viejo), no
       * en la que vio: con `searching` lo encontró filtrando.
       */
      trackReceptorSuggestedSaved: (props: {
        addressBookId: number;
        position: number;
        searching: boolean;
        hadMissingFields: boolean;
      }) =>
        emit(EVENT_TYPES.INVOICE_V2_RECEPTOR_SUGGESTED_SAVED, {
          address_book_id: props.addressBookId,
          position: props.position,
          searching: props.searching,
          suggestion_source: 'financials',
          had_missing_fields: props.hadMissingFields,
        }),

      /**
       * La baja del sello, ya confirmada por el DELETE. Es un hecho que el fiscal-engine no
       * emite y la baja borra sus filas de `invoicing.certificate`: este es su único registro.
       */
      trackCsdRemoved: (reason: CsdRemovalReason, entry: StampsEntry) =>
        emit(EVENT_TYPES.INVOICE_V2_CSD_REMOVED, { reason, entry }),

      /**
       * El aviso de que el SAT todavía no acepta el sello, al entrar a la facturación.
       * `hours_since`: desde la emisión del certificado o desde el último rechazo por
       * LCO, según `reason`. El hecho (el rechazo) vive en el servidor; esto mide quién
       * vio el aviso, para cruzarlo con los intentos que se ahorraron.
       */
      trackCsdActivationPendingShown: (props: {
        reason: 'new_certificate' | 'lco_rejection' | null;
        hoursSince: number | null;
      }) =>
        emit(EVENT_TYPES.INVOICE_V2_CSD_ACTIVATION_PENDING_SHOWN, {
          surface: 'module_entry',
          reason: props.reason,
          hours_since: props.hoursSince,
        }),

      trackFileDownloaded: (invoiceUuid: string | undefined, kind: StampedFileKind) =>
        emit(EVENT_TYPES.INVOICE_V2_FILE_DOWNLOADED, { invoice_uuid: invoiceUuid, kind }),

      /**
       * La escena del timbrado lleva 8 s sin respuesta y enseña «El SAT está tardando».
       * Con `draft_id` cruza con `invoice_v2_stamp_requested` y con el desenlace del servidor.
       */
      trackStampSlowShown: (props: { draftId: string; isTest: boolean }) =>
        emit(EVENT_TYPES.INVOICE_V2_STAMP_SLOW_SHOWN, { draft_id: props.draftId, is_test: props.isTest }),

      /**
       * Lo que se hizo con la factura en la pantalla de éxito. `kind` en las acciones de un
       * archivo; `from` si el visor se abrió tocando la fila o desde el menú;
       * `share_outcome` si la hoja del sistema la compartió o se descargó por no haber.
       * «Facturarle otra» desde el podio no sale aquí: es `flow_started` con `entry: stamped_podium`.
       */
      trackStampedAction: (props: {
        invoiceUuid?: string;
        action: StampedAction;
        kind?: StampedFileKind;
        from?: 'row' | 'menu';
        shareOutcome?: StampedShareOutcome;
      }) =>
        emit(EVENT_TYPES.INVOICE_V2_STAMPED_ACTION, {
          invoice_uuid: props.invoiceUuid,
          action: props.action,
          ...(props.kind ? { kind: props.kind } : {}),
          ...(props.from ? { from: props.from } : {}),
          ...(props.shareOutcome ? { share_outcome: props.shareOutcome } : {}),
        }),

      /**
       * Abrió el detalle de una barra del podio. `place` 1 a 3; `just_invoiced` si es el
       * cliente de la factura recién timbrada; `podium_size` las barras que había y
       * `client_count` los clientes del mes. Sin RFC ni nombre.
       */
      trackPodiumOpened: (props: {
        place: number;
        justInvoiced: boolean;
        podiumSize: number;
        clientCount: number;
      }) =>
        emit(EVENT_TYPES.INVOICE_V2_PODIUM_OPENED, {
          place: props.place,
          just_invoiced: props.justInvoiced,
          podium_size: props.podiumSize,
          client_count: props.clientCount,
        }),

      trackAssistantOpened: (props: {
        conversationId: string;
        draftId: string;
        step: AssistantStep;
        newConversation: boolean;
      }) =>
        once(`assistant_opened:${props.conversationId}`, () =>
          emit(EVENT_TYPES.INVOICE_V2_ASSISTANT_OPENED, {
            draft_id: props.draftId,
            step: props.step,
            new_conversation: props.newConversation,
          }),
        ),

      trackAssistantDraftApplied: (props: {
        draftId: string;
        sessionId?: string;
        turnIndex?: number;
        fieldsApplied: number;
        undone: boolean;
      }) =>
        emit(EVENT_TYPES.INVOICE_V2_ASSISTANT_DRAFT_APPLIED, {
          draft_id: props.draftId,
          session_id: props.sessionId,
          turn_index: props.turnIndex,
          fields_applied: props.fieldsApplied,
          undone: props.undone,
        }),

      trackQrDashboardViewed: (config: QrOwnerConfig, engine: QrEngine, entry: QrConfigEntry) =>
        emit(EVENT_TYPES.INVOICE_V2_QR_DASHBOARD_VIEWED, {
          slug: config.slug,
          status: config.status,
          engine,
          csd_status: config.csdStatus,
          product_count: config.products.length,
          entry,
        }),

      /** El prellenado con el oficial. Sin el monto: solo moneda, fuente y si fue día inhábil. */
      trackExchangeRatePrefilled: (props: {
        draftId: string;
        currency: string;
        fuente: string;
        diaInhabil: boolean | null;
      }) =>
        emit(EVENT_TYPES.INVOICE_V2_EXCHANGE_RATE_PREFILLED, {
          draft_id: props.draftId,
          currency: props.currency,
          fuente: props.fuente,
          dia_inhabil: props.diaInhabil,
        }),

      trackExchangeRateOverridden: (draftId: string, currency: string) =>
        once(`exchange_rate_overridden:${draftId}`, () =>
          emit(EVENT_TYPES.INVOICE_V2_EXCHANGE_RATE_OVERRIDDEN, { draft_id: draftId, currency }),
        ),

      trackExchangeRateUnavailable: (props: {
        draftId: string;
        currency: string;
        code: TipoCambioErrorCode;
      }) =>
        emit(EVENT_TYPES.INVOICE_V2_EXCHANGE_RATE_UNAVAILABLE, {
          draft_id: props.draftId,
          currency: props.currency,
          code: props.code,
        }),

      /** La vista previa del PDF sin timbrar, en revisión. `error` = el motor la rechazó o no respondió. */
      trackPreviewOpened: (props: { draftId: string; engine: StampEngine; outcome: 'ok' | 'error' }) =>
        emit(EVENT_TYPES.INVOICE_V2_PREVIEW_OPENED, {
          draft_id: props.draftId,
          engine: props.engine,
          outcome: props.outcome,
        }),

      /**
       * La pantalla de la marca del PDF cargó datos: el denominador del funnel. Cuántos
       * guardaron una marca lo dice `pdfbrand.brand_change`, no este evento.
       */
      trackPdfBrandViewed: (props: { entry: PdfBrandEntry; hasCustomBrand: boolean }) =>
        emit(EVENT_TYPES.INVOICE_V2_PDF_BRAND_VIEWED, {
          entry: props.entry,
          has_custom_brand: props.hasCustomBrand,
        }),

      /**
       * Terminó un intento de subir logo. `rejected`: el archivo no sirve (`client_*` si lo
       * detectó el navegador, `pdf_marca_*` si el servidor); `error`: no se pudo guardar.
       * `mime` es el subtipo del archivo (`png`, `jpeg`…).
       */
      trackPdfBrandLogoUploaded: (props: {
        outcome: 'ok' | 'rejected' | 'error';
        errorCode?: string;
        sizeKb: number;
        mime: string;
      }) =>
        emit(EVENT_TYPES.INVOICE_V2_PDF_BRAND_LOGO_UPLOADED, {
          outcome: props.outcome,
          ...(props.errorCode ? { error_code: props.errorCode.toLowerCase() } : {}),
          size_kb: props.sizeKb,
          mime: props.mime,
        }),

      /** La vista previa de la marca. `dirty`: el borrador tenía cambios sin guardar. */
      trackPdfBrandPreviewOpened: (props: { outcome: 'ok' | 'error'; hasLogo: boolean; dirty: boolean }) =>
        emit(EVENT_TYPES.INVOICE_V2_PDF_BRAND_PREVIEW_OPENED, {
          outcome: props.outcome,
          has_logo: props.hasLogo,
          dirty: props.dirty,
        }),

      /**
       * Terminó un guardado. El número de marcas guardadas sale de `pdfbrand.brand_change`;
       * esto dice qué se cambió y por qué falló. `colors_adjusted`: el PDF oscurece alguno.
       */
      trackPdfBrandSaved: (props: {
        outcome: 'ok' | 'error';
        errorCode?: string;
        hasLogo: boolean;
        fieldsChanged: PdfBrandFieldName[];
        colorsAdjusted?: boolean;
      }) =>
        emit(EVENT_TYPES.INVOICE_V2_PDF_BRAND_SAVED, {
          outcome: props.outcome,
          ...(props.errorCode ? { error_code: props.errorCode.toLowerCase() } : {}),
          has_logo: props.hasLogo,
          fields_changed: props.fieldsChanged,
          ...(props.colorsAdjusted === undefined ? {} : { colors_adjusted: props.colorsAdjusted }),
        }),

      trackPdfBrandReset: (outcome: 'ok' | 'error') =>
        emit(EVENT_TYPES.INVOICE_V2_PDF_BRAND_RESET, { outcome }),

      /** El emisor cambió en revisión el régimen propuesto (claves c_RegimenFiscal); elegir el mismo no cuenta. */
      trackIssuerRegimeChanged: (props: {
        draftId: string;
        fromRegime: string;
        toRegime: string;
        regimesCount: number;
      }) => {
        if (props.fromRegime === props.toRegime) return;
        emit(EVENT_TYPES.INVOICE_V2_ISSUER_REGIME_CHANGED, {
          draft_id: props.draftId,
          from_regime: props.fromRegime,
          to_regime: props.toRegime,
          regimes_count: props.regimesCount,
        });
      },

      /**
       * El emisor cambió el CP desde donde factura (el lugar de expedición). Sin el
       * CP: es un dato de su domicilio. `to_profile`: regresó al de su constancia.
       */
      trackIssuerPostalCodeChanged: (props: { draftId: string; toProfile: boolean }) =>
        emit(EVENT_TYPES.INVOICE_V2_ISSUER_POSTAL_CODE_CHANGED, {
          draft_id: props.draftId,
          to_profile: props.toProfile,
        }),

      trackQrShared: (slug: string, channel: QrShareChannel, content: QrShareContent) =>
        emit(EVENT_TYPES.INVOICE_V2_QR_SHARED, { slug, channel, content }),

      /** El dueño abrió «Mostrar QR» para que un cliente lo escanee de su pantalla. */
      trackQrShowQr: (slug: string, engine: QrEngine) =>
        emit(EVENT_TYPES.INVOICE_V2_QR_SHOW_QR, { slug, engine }),

      /**
       * «Quiero ver cómo funciona»: en la tarjeta del QR del inicio (`home`) o bajo el
       * título de «Mi página de facturación» (`qr_page`). `has_page`: si su página ya
       * está publicada.
       */
      trackQrVideoOpened: (entry: QrVideoOpenedEntry, hasPage: boolean) =>
        emit(EVENT_TYPES.INVOICE_V2_QR_VIDEO_OPENED, { entry, has_page: hasPage }),

      /** El listado completo de solicitudes, una vez por visita, con lo que había al abrirlo. */
      trackQrRequestsViewed: (props: { engine: QrEngine; totalCount: number; pendingCount: number }) =>
        emit(EVENT_TYPES.INVOICE_V2_QR_REQUESTS_VIEWED, {
          engine: props.engine,
          total_count: props.totalCount,
          pending_count: props.pendingCount,
        }),

      trackQrRequestOpened: (props: {
        requestId: number;
        status: InvoiceRequestStatus;
        from: QrRequestOpenedFrom;
        engine: QrEngine;
      }) =>
        emit(EVENT_TYPES.INVOICE_V2_QR_REQUEST_OPENED, {
          request_id: props.requestId,
          status: props.status,
          from: props.from,
          engine: props.engine,
        }),

      /** El dueño guardó una corrección de los conceptos; `status` es el estado desde el que editó. */
      trackQrRequestEdited: (props: {
        requestId: number;
        status: InvoiceRequestStatus;
        engine: QrEngine;
        conceptsCount: number;
      }) =>
        emit(EVENT_TYPES.INVOICE_V2_QR_REQUEST_EDITED, {
          request_id: props.requestId,
          status: props.status,
          engine: props.engine,
          concepts_count: props.conceptsCount,
        }),

      /** Cambió de pestaña en las solicitudes de su página. `request_count`: cuántas trae la elegida. */
      trackQrRequestsTabChanged: (props: { tab: QrRequestsTab; requestCount: number; engine: QrEngine }) =>
        emit(EVENT_TYPES.INVOICE_V2_QR_REQUESTS_TAB_CHANGED, {
          tab: props.tab,
          request_count: props.requestCount,
          engine: props.engine,
        }),

      /**
       * Tocó un mes de «Ingresos por mes» para ver sus facturas (`month_key` = `AAAA-MM`).
       * `period_preset`: el periodo que tenía elegido la tarjeta. Cerrarlo no cuenta.
       */
      trackQrIncomeMonthSelected: (props: {
        monthKey: string;
        periodPreset: PeriodPreset;
        invoiceCount: number;
        engine: QrEngine;
      }) =>
        emit(EVENT_TYPES.INVOICE_V2_QR_INCOME_MONTH_SELECTED, {
          month_key: props.monthKey,
          period_preset: props.periodPreset,
          invoice_count: props.invoiceCount,
          engine: props.engine,
        }),

      trackQrMaintenanceViewed: () =>
        emit(EVENT_TYPES.INVOICE_V2_QR_MAINTENANCE_VIEWED, { surface: 'owner' }),

      /**
       * Cambió el periodo de una lista: «Mis facturas» del home, el panel del QR o el
       * listado de solicitudes. `preset` es `recent` al quitar el filtro; `range_days`
       * cuenta los dos extremos y va vacío sin periodo.
       */
      trackPeriodFilterChanged: (props: {
        surface: PeriodFilterSurface;
        preset: PeriodPreset | 'recent';
        rangeDays: number | null;
      }) =>
        emit(EVENT_TYPES.INVOICE_V2_PERIOD_FILTER_CHANGED, {
          surface: props.surface,
          preset: props.preset,
          range_days: props.rangeDays,
        }),

      /**
       * `/invoice/` no llegó a la facturación a tiempo y el vigía tuvo que actuar:
       * `renavigate` a los 4 s, `hard_redirect` (recarga directa) a los 10 s. Si sale,
       * algo sigue interrumpiendo el salto; `is_native` y `online` ayudan a ubicarlo.
       */
      trackLegacyRedirectStuck: (props: {
        stage: 'renavigate' | 'hard_redirect';
        elapsedMs: number;
        target: string;
        isNative: boolean;
        online: boolean;
      }) =>
        emit(EVENT_TYPES.INVOICE_V2_LEGACY_REDIRECT_STUCK, {
          stage: props.stage,
          elapsed_ms: props.elapsedMs,
          target: props.target,
          is_native: props.isNative,
          online: props.online,
        }),

      /** «Ver mis facturas» en la barra de arriba. `from`: el tipo de pantalla, nunca el folio. */
      trackMyInvoicesClicked: (from: MyInvoicesFrom) =>
        emit(EVENT_TYPES.INVOICE_V2_MY_INVOICES_CLICKED, { from }),

      /**
       * Switched the «Mis facturas» tab; choosing the one already open does not count.
       * `period_preset` is the list's period (`this_year` by default). `invoice_count`:
       * the rows the chosen tab already had cached for that period, or `null` if it was
       * never loaded; nothing is fetched to fill it.
       */
      trackMyInvoicesTabChanged: (props: {
        tab: MyInvoicesTab;
        periodPreset: PeriodPreset;
        invoiceCount: number | null;
      }) =>
        emit(EVENT_TYPES.INVOICE_V2_MY_INVOICES_TAB_CHANGED, {
          tab: props.tab,
          period_preset: props.periodPreset,
          invoice_count: props.invoiceCount,
        }),

      /**
       * Tapped a bar of «Ingresos por mes» in «Mis facturas», which narrows the list to
       * that month (`month_key` = `YYYY-MM`); `invoice_count` is the bar's count. Tapping
       * it again to go back to the year does not count.
       */
      trackMyInvoicesMonthSelected: (props: { monthKey: string; invoiceCount: number }) =>
        emit(EVENT_TYPES.INVOICE_V2_MY_INVOICES_MONTH_SELECTED, {
          month_key: props.monthKey,
          invoice_count: props.invoiceCount,
        }),

      /**
       * A viewer opened an invoice's file from «Mis facturas»: a received invoice or a
       * retention certificate from the list, or «Ver factura en PDF» in the detail.
       * `origin: from_xml` is a PDF the fiscal-engine paints from the stamped XML
       * (`isPdfFromXml`). Only the invoice's kind and source leave: no RFC, name or UUID.
       * Downloads go in `invoice_v2_file_downloaded`.
       */
      trackMyInvoicesDocumentOpened: (invoice: MyInvoice, file: StampedFileKind) =>
        emit(EVENT_TYPES.INVOICE_V2_MY_INVOICES_DOCUMENT_OPENED, {
          kind: invoice.kind,
          file,
          origin: file === 'pdf' && isPdfFromXml(invoice) ? 'from_xml' : 'stored',
          invoice_source: invoice.source,
        }),

      /** El detalle de una factura de «Mis facturas»; `from` dice si llegó del listado o pasando de página. */
      trackInvoiceOpened: (props: { invoiceUuid: string; status: MyInvoiceStatus; from: 'list' | 'pager' }) =>
        emit(EVENT_TYPES.INVOICE_V2_INVOICE_OPENED, {
          invoice_uuid: props.invoiceUuid,
          status: props.status,
          from: props.from,
        }),

      /**
       * Confirmó la cancelación. El desenlace sale aparte, con el motor que contestó.
       * `invoice_source` (heru | otra_cuenta | externa) separa en los tres eventos lo
       * que timbró la cuenta de lo que se cancela sin haberlo timbrado.
       */
      trackCancelRequested: (invoiceUuid: string, reason: CancelReasonCode, invoiceSource: MyInvoiceSource) =>
        emit(EVENT_TYPES.INVOICE_V2_CANCEL_REQUESTED, { invoice_uuid: invoiceUuid, reason, invoice_source: invoiceSource }),

      trackCancelSucceeded: (props: {
        invoiceUuid: string;
        reason: CancelReasonCode;
        invoiceSource: MyInvoiceSource;
        outcome: CancelInvoiceOutcome;
        durationMs: number;
      }) =>
        emit(EVENT_TYPES.INVOICE_V2_CANCEL_SUCCEEDED, {
          invoice_uuid: props.invoiceUuid,
          reason: props.reason,
          invoice_source: props.invoiceSource,
          engine: props.outcome.engine,
          document_status: props.outcome.documentStatus,
          cancellation_status: props.outcome.cancellationStatus,
          duration_ms: props.durationMs,
        }),

      /** `error_code`: el `code` del Go o el `error` del NestJS; si no hay, el status HTTP. */
      trackCancelFailed: (props: {
        invoiceUuid: string;
        reason: CancelReasonCode;
        invoiceSource: MyInvoiceSource;
        engine: CancelEngine;
        errorCode: string;
        durationMs: number;
      }) =>
        emit(EVENT_TYPES.INVOICE_V2_CANCEL_FAILED, {
          invoice_uuid: props.invoiceUuid,
          reason: props.reason,
          invoice_source: props.invoiceSource,
          engine: props.engine,
          error_code: props.errorCode,
          duration_ms: props.durationMs,
        }),

      /**
       * Se abrió el registro de un pago. `pendingCount` solo si la lista de PPD con
       * saldo ya estaba cargada al abrirlo (desde el detalle de una factura, sí).
       */
      trackPaymentStarted: (props: {
        paymentDraftId: string;
        entry: PaymentEntry;
        pendingCount?: number;
        preselected: boolean;
      }) =>
        emit(EVENT_TYPES.INVOICE_V2_PAYMENT_STARTED, {
          payment_draft_id: props.paymentDraftId,
          entry: props.entry,
          preselected: props.preselected,
          ...(props.pendingCount === undefined ? {} : { pending_count: props.pendingCount }),
        }),

      trackPaymentStepViewed: (paymentDraftId: string, step: PaymentStepViewed) =>
        once(`payment_step_viewed:${paymentDraftId}:${step}`, () =>
          emit(EVENT_TYPES.INVOICE_V2_PAYMENT_STEP_VIEWED, { payment_draft_id: paymentDraftId, step }),
        ),

      /** El usuario pidió timbrar. Sin RFC ni nombre: origen de la factura, parcialidad y monto. */
      trackPaymentStampRequested: (props: {
        paymentDraftId: string;
        invoice: PendingPaymentInvoice;
        amount: number;
        balanceBefore: number;
        paymentForm: string;
        isTest: boolean;
      }) => {
        const attempt = (stampAttempts.get(props.paymentDraftId) ?? 0) + 1;
        stampAttempts.set(props.paymentDraftId, attempt);
        emit(EVENT_TYPES.INVOICE_V2_PAYMENT_STAMP_REQUESTED, {
          payment_draft_id: props.paymentDraftId,
          attempt,
          is_test: props.isTest,
          amount: props.amount,
          is_partial: props.amount < props.balanceBefore,
          installment: props.invoice.next_installment,
          payment_form: props.paymentForm,
          parent_source: props.invoice.source ?? 'unknown',
          tax_profile: props.invoice.tax_profile ?? 'unknown',
        });
      },

      /**
       * Lo que vio el usuario, incluido lo que el servidor no puede contar: un
       * `uncertain` sin respuesta del todo. El número de complementos sale del libro.
       */
      trackPaymentStampResult: (props: {
        paymentDraftId: string;
        outcome: PaymentStampOutcome;
        errorCode?: string | null;
      }) =>
        emit(EVENT_TYPES.INVOICE_V2_PAYMENT_STAMP_RESULT, {
          payment_draft_id: props.paymentDraftId,
          outcome: props.outcome,
          ...(props.errorCode ? { error_code: props.errorCode.toLowerCase() } : {}),
        }),

      /**
       * El aviso de cupo agotado al entrar al wizard, antes de capturar. Una vez por
       * sesión y plan. Cuántos se toparon de verdad lo dice
       * `invoiceplan.quota_rejection`; esto mide a quién se le adelantó el aviso.
       */
      trackQuotaPaywallShown: (snapshot: QuotaSnapshot) =>
        once(`quota_paywall:${snapshot.planCode}:${snapshot.limit}`, () =>
          emit(EVENT_TYPES.INVOICE_QUOTA_PAYWALL_SHOWN, quotaProps(snapshot)),
        ),

      /**
       * Opened the sheet of the home's plan chip, with the plan, where it comes from and
       * the usage it showed. Every tap counts. `plan_source` and not `source`: the
       * transport overwrites `source` with the platform.
       */
      trackPlanStatusOpened: (props: QuotaSnapshot & { source: QuotaPlanSource; status: QuotaStatus }) =>
        emit(EVENT_TYPES.INVOICE_PLAN_STATUS_OPENED, {
          ...quotaProps(props),
          plan_source: props.source,
          quota_status: props.status,
        }),

      /**
       * Tapped a way to the invoicing plans: «Ver planes» from the paywall, the
       * stamping error or the upsell of a feature (`feature`), «Mejorar plan» from the
       * plan sheet; with `origin: plans` the
       * user chose one, and it carries which, its periodicity and how the screen was
       * reached. The purchase is measured in `plans_service`, not here.
       */
      trackPlanUpgradeClicked: (
        props: QuotaSnapshot & {
          origin: QuotaUpgradeOrigin;
          targetPlanCode?: string;
          periodicity?: 'monthly' | 'annual';
          entry?: InvoicePlansEntry;
          /** With `origin: feature`: the feature of the upsell (`client_email`, `qr_page`…). */
          feature?: string;
        },
      ) =>
        emit(EVENT_TYPES.INVOICE_PLAN_UPGRADE_CLICKED, {
          ...quotaProps(props),
          origin: props.origin,
          ...(props.feature ? { feature: props.feature } : {}),
          ...(props.targetPlanCode ? { target_plan_code: props.targetPlanCode } : {}),
          ...(props.periodicity ? { periodicity: props.periodicity } : {}),
          ...(props.entry ? { entry: props.entry } : {}),
        }),
    }),
    [emit],
  );
};

/** La página pública del QR: un cliente sin cuenta, así que solo Amplitude y con `device_id`. */
export const useInvoiceQrPublicTracking = () => {
  const { trackEvent } = useAmplitude();

  const emit = useCallback(
    (eventName: string, properties: Record<string, unknown>) => {
      void trackEvent(eventName, properties);
    },
    [trackEvent],
  );

  return useMemo(
    () => ({
      trackPublicViewed: (props: {
        slug: string;
        engine: QrEngine;
        status: QrPublicLinkStatus;
        returning: boolean;
        entry: QrPublicEntry;
      }) => emit(EVENT_TYPES.INVOICE_V2_QR_PUBLIC_VIEWED, { ...props }),

      /**
       * La intención del cliente. El hecho —con revisión, `receiver_source` y
       * `concept_kind`— lo emite el servidor como `invoice_v2_qr_request_created`.
       */
      trackRequestSubmitted: (props: {
        slug: string;
        publicId: string;
        correction: boolean;
        productCount: number;
        total: number;
      }) =>
        emit(EVENT_TYPES.INVOICE_V2_QR_REQUEST_SUBMITTED, {
          slug: props.slug,
          public_id: props.publicId,
          correction: props.correction,
          product_count: props.productCount,
          total: props.total,
        }),

      trackFiscalValidation: (props: {
        slug: string;
        outcome: QrFiscalValidationOutcome;
        field: QrSubmitField;
        trigger: QrFiscalValidationTrigger;
      }) => emit(EVENT_TYPES.INVOICE_V2_QR_FISCAL_VALIDATION, { ...props }),

      trackRequestSubmitFailed: (props: {
        slug: string;
        errorCode: QrSubmitErrorCode;
        field: QrSubmitField;
      }) =>
        emit(EVENT_TYPES.INVOICE_V2_QR_REQUEST_SUBMIT_FAILED, {
          slug: props.slug,
          error_code: props.errorCode,
          field: props.field,
        }),

      trackReceiptViewed: (props: {
        publicId: string;
        status?: QrRequestStatus;
        hasClaim: boolean;
        entry: QrReceiptEntry;
      }) =>
        emit(EVENT_TYPES.INVOICE_V2_QR_RECEIPT_VIEWED, {
          public_id: props.publicId,
          status: props.status,
          has_claim: props.hasClaim,
          entry: props.entry,
        }),

      trackReceiptDownloaded: (publicId: string, kind: QrReceiptFileKind) =>
        emit(EVENT_TYPES.INVOICE_V2_QR_RECEIPT_DOWNLOADED, { public_id: publicId, kind }),

      /** «Conoce heru» desde el estado de la solicitud: la página pública como canal de adquisición. */
      trackHeruCtaClicked: (publicId: string, status?: QrRequestStatus) =>
        emit(EVENT_TYPES.INVOICE_V2_QR_HERU_CTA_CLICKED, { public_id: publicId, status }),

      /** Sin `slug` en `/factura-qr/s/:publicId`: esa ruta no lo trae. */
      trackMaintenanceViewed: (slug?: string) =>
        emit(EVENT_TYPES.INVOICE_V2_QR_MAINTENANCE_VIEWED, {
          surface: 'public',
          ...(slug ? { slug } : {}),
        }),
    }),
    [emit],
  );
};

export { useTrackView } from '@/hooks/useTrackView';
